import { type ReactNode } from 'react'
import type { ComponentType } from 'react'
import { NavLink, useLocation, type NavLinkProps } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { useEdition } from '../../edition/EditionProvider'
import type { DockIconKey, DockTab } from '../../lib/edition'
import {
  BasketIcon,
  BoltIcon,
  CardIcon,
  ChatIcon,
  ChefHatIcon,
  HomeIcon,
  type IconProps,
  LifebuoyIcon,
  PlateIcon,
  RouteIcon,
  ShieldIcon,
  StorefrontIcon,
  UserIcon,
  WalletIcon,
} from '../icons'

/*
 * Linaria's styled() needs a string className; NavLink also accepts a function of
 * its active state. Only the string form is used here.
 */
const NavLinkBase = NavLink as ComponentType<Omit<NavLinkProps, 'className'> & { className?: string }>


const GLYPH: Record<DockIconKey, (props: IconProps) => ReactNode> = {
  home: HomeIcon,
  chef: ChefHatIcon,
  dish: PlateIcon,
  market: StorefrontIcon,
  wallet: CardIcon,
  inbox: ChatIcon,
  help: LifebuoyIcon,
  cart: BasketIcon,
  profile: UserIcon,
  offers: BoltIcon,
  active: RouteIcon,
  earnings: WalletIcon,
  safety: ShieldIcon,
}

const Style = {
  Bar: styled.nav`
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: stretch;
    padding: 6px 2px calc(6px + env(safe-area-inset-bottom, 0px));
    background: ${minimalVars.color.bgSurface};
    border-top: 1px solid ${minimalVars.color.borderSubtle};
  `,
  Tab: styled(NavLinkBase)`
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 1px;
    /* The dock is the app's primary navigation and is tapped constantly, so it
       is held to the 44px comfortable-target size rather than the 24px WCAG
       2.5.8 floor. The icon-plus-label stack happened to measure 42.5px, which
       is close enough to feel fine and just small enough to miss. */
    min-height: 44px;
    text-decoration: none;
    color: ${minimalVars.color.textTertiary};
    border-radius: ${minimalVars.radius.sm};
    transition: color 180ms ease;

    &.active {
      color: ${minimalVars.color.brand};
    }

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.borderFocus};
      outline-offset: -2px;
    }
  `,
  IconWrap: styled.span`
    position: relative;
    display: inline-flex;
  `,
  Count: styled.span`
    position: absolute;
    top: -6px;
    left: 60%;
    min-width: 15px;
    height: 15px;
    padding: 0 4px;
    border-radius: 999px;
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    /* A 9px count inside a fixed 15px pill: metaSize would overflow the line. */
    font-size: 9px;
    font-weight: ${minimalVars.typography.weightBold};
    line-height: 15px;
    text-align: center;
  `,
  Label: styled.span`
    /* An 11px label in a 44px dock target beside a 20px icon: metaSize would
       clip the longest tab names. */
    font-size: 11px;
    font-weight: ${minimalVars.typography.weightSemibold};
    letter-spacing: 0.01em;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  `,
}

/**
 * Bottom tab dock — a flex child pinned to the bottom of the device frame (its
 * width therefore matches the column, not the viewport). Tabs come from the
 * active edition's `dockTabs`; the active tab takes the edition accent.
 */
export const AppDock = ({
  badges,
  tabs,
}: {
  badges?: Record<string, number>
  /** Override the edition's default tabs (e.g. ChooseChow chef navigation). */
  tabs?: readonly DockTab[]
}) => {
  const { profile } = useEdition()
  const { pathname } = useLocation()
  const dockTabs = tabs ?? profile.dockTabs
  return (
    <Style.Bar aria-label="Primary">
      {dockTabs.map((tab) => {
        const Glyph = GLYPH[tab.icon]
        const count = badges?.[tab.path] ?? 0
        return (
          <Style.Tab key={tab.path} to={tab.path} className={tab.path === '/dishes' && ['/menus', '/kitchens'].includes(pathname) ? 'active' : undefined} end={tab.path === '/' || tab.path === '/dashboard'}>
            <Style.IconWrap>
              <Glyph size="md" />
              {count > 0 ? <Style.Count aria-label={`${count} item${count === 1 ? '' : 's'}`}>{count}</Style.Count> : null}
            </Style.IconWrap>
            <Style.Label>{tab.label}</Style.Label>
          </Style.Tab>
        )
      })}
    </Style.Bar>
  )
}
